import { Injectable } from '@angular/core'
import { Capacitor } from '@capacitor/core'
import { LiveUpdate } from '@capawesome/capacitor-live-update'
import { Observable, Subject } from 'rxjs'
import { environment } from 'src/environments/environment'

export type LiveUpdateEvent =
  | 'check_started'
  | 'bundle_found'
  | 'bundle_not_found'
  | 'download_started'
  | 'download_completed'
  | 'reload_started'
  | 'reload_deferred'
  | 'error'

@Injectable({
  providedIn: 'root',
})
export class LiveUpdateService {
  private readyCalled = false

  constructor() {}

  // Sujet privé pour émettre les événements
  private updateSubject = new Subject<LiveUpdateEvent>()

  // Observable public pour que les composants puissent s'abonner
  public updates$: Observable<LiveUpdateEvent> =
    this.updateSubject.asObservable()

  /**
   * Vérifie et applique les mises à jour.
   *
   * @param canReload Consulté juste avant le redémarrage. Le téléchargement
   * peut durer plus longtemps que l'écran de lancement ne l'attend : passé ce
   * point, redémarrer interromprait l'utilisateur en pleine navigation.
   */
  async checkForUpdate(canReload: () => boolean = () => true) {
    try {
      this.updateSubject.next('check_started')

      // Recherche du dernier bundle disponible
      const bundle = await LiveUpdate.fetchLatestBundle()
      if (
        bundle &&
        bundle.bundleId &&
        bundle.customProperties?.['timestamp'] &&
        environment.timestamp < bundle.customProperties?.['timestamp']
      ) {
        const currentBundles = await LiveUpdate.getBundles()
        const currentBundle = await LiveUpdate.getCurrentBundle()

        if (
          currentBundles.bundleIds.includes(bundle.bundleId) &&
          currentBundle.bundleId === bundle.bundleId
        ) {
          this.updateSubject.next('bundle_not_found')
          return
        } else {
          this.updateSubject.next('bundle_found')
        }

        // Démarrage du téléchargement du bundle
        this.updateSubject.next('download_started')

        const syncResult = await LiveUpdate.sync()
        if (syncResult.nextBundleId) {
          this.updateSubject.next('download_completed')

          // `sync()` a déjà désigné le bundle suivant : il s'appliquera au
          // prochain démarrage à froid. On ne redémarre donc pas ici quand
          // l'écran de lancement a rendu la main - un téléchargement lent se
          // terminait sinon en plein parcours, et `LiveUpdate.reload()`
          // relançait l'application sous l'utilisateur, splash compris.
          if (!canReload()) {
            this.updateSubject.next('reload_deferred')
            return
          }

          // Pause de 500ms avant le redémarrage pour laisser le temps à l'interface utilisateur de réagir
          // Et que ca ne fasse pas un clignotement non plus
          await new Promise((resolve) => setTimeout(resolve, 500))
          this.updateSubject.next('reload_started')
          await LiveUpdate.reload()
        } else {
          this.updateSubject.next('bundle_not_found')
        }
      } else {
        this.updateSubject.next('bundle_not_found')
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour LiveUpdate :', error)
      this.updateSubject.next('error')
    }
  }

  /**
   * Signale au plugin que le bundle courant fonctionne.
   *
   * À appeler au plus tôt, et depuis le démarrage de l'application plutôt que
   * depuis une page : le plugin arme un minuteur de rollback à la création du
   * processus (`readyTimeout`, 10 s ici) et, s'il expire, rebascule sur le
   * bundle par défaut - ce qui repositionne le chemin servi par la WebView,
   * donc relance l'application. Or toutes les entrées ne passent pas par
   * l'écran de lancement : retour Stripe, session SSO, lien profond.
   *
   * Idempotent, sans effet hors natif, et ne rejette pas : les appelants -
   * écran de lancement comme garde de routage - n'ont rien à en attendre.
   */
  async ready() {
    if (this.readyCalled || !Capacitor.isNativePlatform()) {
      return
    }
    this.readyCalled = true

    let result
    try {
      result = await LiveUpdate.ready()
    } catch (error) {
      console.error('LiveUpdate.ready() a échoué :', error)
      return
    }

    // Quel code tourne réellement. Un binaire fraîchement installé par-dessus
    // une installation existante continue de servir le dernier bundle OTA
    // téléchargé : sans cette ligne, on débogue du code que l'appareil
    // n'exécute pas. `bundleId` nul = bundle livré avec le binaire.
    console.log(
      `[LiveUpdate] bundle servi : ${
        result.currentBundleId ?? 'par défaut (binaire)'
      }`
    )
    if (result.rollback) {
      console.warn(
        `[LiveUpdate] l'application a redémarré sur un rollback ; bundle abandonné : ${result.previousBundleId}`
      )
    }
  }
}
